import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles, ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { shopApi, errText } from "@/lib/shopAuth";
import { useCart } from "@/lib/shop";

const SKEY = "empro_shop_assistant_session";
const newSession = () => `sa-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const SUGGESTIONS = ["Pokaż koszulki", "Jaki jest czas i koszt dostawy?", "Co z moim zamówieniem?", "Jak zwrócić produkt?"];

export default function ShopAssistant() {
  const { add } = useCart();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => localStorage.getItem(SKEY) || newSession());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { localStorage.setItem(SKEY, sessionId); }, [sessionId]);
  useEffect(() => {
    if (!open || messages.length) return;
    shopApi.get("/shop/assistant/history", { params: { session_id: sessionId } })
      .then((r) => setMessages(r.data?.messages || [])).catch(() => {});
  }, [open]); // eslint-disable-line
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, busy, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await shopApi.post("/shop/assistant/chat", { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, cart_intent: data.cart_intent }]);
      if (data.cart_intent) {
        const ci = data.cart_intent;
        add({ product_id: ci.product_id, name: ci.name, photo: ci.photo || null, color: ci.color, size: ci.size, quantity: 1, price_grosz: ci.price_grosz });
        toast.success(`Dodano do koszyka: ${ci.name}`);
      }
    } catch (e) {
      toast.error(errText(e));
      setMessages((m) => [...m, { role: "assistant", content: "Przepraszam, chwilowy problem. Spróbuj ponownie.", error: true }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button data-testid="shop-assistant-open" onClick={() => setOpen(true)} aria-label="Asystent sklepu"
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-amber-400 px-5 py-3.5 font-bold text-[#0D1117] shadow-lg shadow-black/40 hover:bg-amber-300 transition-transform hover:scale-105">
          <Sparkles className="h-5 w-5" /><span className="hidden sm:inline">Asystent</span>
        </button>
      )}
      {open && (
        <div data-testid="shop-assistant-panel" className="fixed bottom-5 right-5 z-50 flex w-[calc(100vw-2.5rem)] max-w-[380px] flex-col rounded-2xl border border-white/10 bg-[#0D1117] shadow-2xl shadow-black/60" style={{ height: "min(560px, calc(100vh - 2.5rem))" }}>
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-amber-400 text-[#0D1117]"><Sparkles className="h-4 w-4" /></span>
              <div><p className="font-display font-extrabold uppercase tracking-tight text-sm leading-none text-neutral-100">Asystent sklepu</p><p className="text-[11px] text-neutral-400 mt-0.5">Produkty · zamówienia · dostawa</p></div>
            </div>
            <button data-testid="shop-assistant-close" onClick={() => setOpen(false)} className="rounded-md p-1.5 text-neutral-400 hover:bg-white/10 hover:text-neutral-100"><X className="h-4 w-4" /></button>
          </div>

          <div ref={scrollRef} data-testid="shop-assistant-messages" className="flex-1 overflow-y-auto px-3.5 py-3.5 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-neutral-400 mt-4">
                <MessageCircle className="mx-auto h-7 w-7 text-amber-400/70 mb-2" />
                <p className="text-sm font-semibold text-neutral-100">Jak mogę pomóc?</p>
                <div className="mt-3 flex flex-wrap justify-center gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid="shop-assistant-suggestion" onClick={() => send(s)} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-neutral-200 hover:border-amber-400/60 hover:text-amber-400 transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`shop-assistant-msg-${m.role}`} className={`max-w-[88%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-amber-400 text-[#0D1117] font-medium" : m.error ? "bg-rose-500/10 border border-rose-500/30 text-rose-300" : "bg-white/5 border border-white/10 text-neutral-100"}`}>
                  {m.content}
                  {m.role === "assistant" && m.cart_intent && (
                    <Link to="/sklep/koszyk" onClick={() => setOpen(false)} className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-400 px-3 py-1.5 text-xs font-bold text-[#0D1117] hover:bg-amber-300"><ShoppingCart className="h-3.5 w-3.5" />Przejdź do koszyka</Link>
                  )}
                </div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="rounded-2xl bg-white/5 border border-white/10 px-3.5 py-2"><Loader2 className="h-4 w-4 animate-spin text-amber-400" /></div></div>}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-white/10 p-3 flex gap-2">
            <input data-testid="shop-assistant-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Napisz wiadomość…" disabled={busy}
              className="flex-1 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:border-amber-400/60" />
            <button data-testid="shop-assistant-send" type="submit" disabled={busy || !input.trim()} className="grid place-items-center rounded-lg bg-amber-400 px-3 text-[#0D1117] hover:bg-amber-300 disabled:opacity-50"><Send className="h-4 w-4" /></button>
          </form>
        </div>
      )}
    </>
  );
}
